/**
 * suggestedPrompts — what the home screen offers before the practitioner has typed.
 *
 * TWO SOURCES, in priority order:
 *
 *   1. THE REAL CATALOGUE. `GET /practitioner/xoe/conversations/{uuid}/tools` returns the
 *      actions this conversation's resolved expert can propose, each with an editable
 *      `starter_template`. That is the honest answer to "what can Xoe do here".
 *   2. A STATIC FALLBACK, because that endpoint needs a conversation uuid and the home
 *      screen's main case — a fresh session, or "New chat" — has none.
 *
 * *** WHAT WAS REJECTED, AND WHY ***
 *
 *   - CREATING a conversation just to get a uuid. This violates X4's central rule
 *     ("STAGED, NEVER CREATED"): ordinary browsing must not write a row. Filling the rail
 *     with empty conversations so the home screen can fetch suggestions is exactly backwards.
 *   - BORROWING `conversations[0].uuid`. The endpoint is scoped to THAT conversation's
 *     resolved expert, so it would show the wrong persona's tools, spend a request keyed on
 *     a conversation the practitioner is not in, and still need a fallback for a new tenant
 *     with no conversations at all.
 *   - A new unscoped `GET /practitioner/xoe/tools` route. That is the right long-term fix
 *     and is filed as a follow-up; it is a backend change and this is a frontend ticket.
 *
 * ⚠️ `patientLabel` DECIDES WHETHER THE PATIENT CARDS RENDER, AND IS NEVER INTERPOLATED
 * INTO A TEMPLATE. A starter template becomes turn text; a patient's name must reach the
 * agent through the conversation BINDING, never through the message body. The label is
 * already on screen in the header chip, so using it as a boolean here adds no exposure.
 *
 * Copy follows design-system.md §23: plain, imperative, no marketing hype, no exclamation
 * points, no emoji.
 */

import type { XoeTool } from '../../types/xoe/XoeTypes';
import type { PromptCard, PromptContext } from '../../types/xoe/XoeUiTypes';

/**
 * A wall of suggestions is a menu, not a suggestion. Six is enough to show the range
 * without asking the practitioner to read a list before they can type.
 */
export const MAX_PROMPT_CARDS = 6;

const GENERAL_FALLBACK: PromptCard[] = [
	{
		key: 'literature',
		label: 'Summarise recent research',
		description: 'Pull the current literature on a topic',
		template: 'What does the current literature say about ',
		kind: 'general',
	},
	{
		key: 'lab',
		label: 'Explain a lab result',
		description: 'Interpret a marker and its clinical range',
		template: 'Explain what an elevated ',
		kind: 'general',
	},
	{
		key: 'plain-language',
		label: 'Draft patient-facing wording',
		description: 'Turn a clinical note into plain language',
		template: 'Rewrite this for a patient to read: ',
		kind: 'general',
	},
	{
		key: 'interaction',
		label: 'Check an interaction',
		description: 'Look for interactions between two agents',
		template: 'Are there interactions between ',
		kind: 'general',
	},
];

/** Offered only when a patient is bound or staged — otherwise "this patient" means nothing. */
const PATIENT_FALLBACK: PromptCard[] = [
	{
		key: 'summarise-record',
		label: 'Summarise this record',
		description: 'Recent history, medications and results',
		template: "Summarise this patient's recent history.",
		kind: 'general',
	},
	{
		key: 'next-steps',
		label: 'Suggest next steps',
		description: "Options to consider from what's on file",
		template: "Based on this patient's record, what should I consider next?",
		kind: 'general',
	},
];

export const fallbackPromptCards = ({ hasPatient }: PromptContext): PromptCard[] =>
	(hasPatient ? [...PATIENT_FALLBACK, ...GENERAL_FALLBACK] : GENERAL_FALLBACK).slice(
		0,
		MAX_PROMPT_CARDS,
	);

/**
 * Map the clinic's real tool catalogue onto cards.
 *
 * Tools with no `starter_template` are dropped: a card that inserts nothing is a card that
 * does nothing when clicked. Tools explicitly `enabled: false` are dropped too — the clinic
 * has turned them off, and offering them would be advertising a permission the practitioner
 * does not have.
 */
export const toolsToPromptCards = (tools: XoeTool[]): PromptCard[] =>
	tools
		.filter((t) => t.enabled !== false && !!t.starter_template)
		.slice(0, MAX_PROMPT_CARDS)
		.map((t) => ({
			key: t.action_type,
			label: t.label,
			description: t.description,
			template: t.starter_template,
			kind: t.kind,
			actionType: t.action_type,
		}));

/**
 * The cards to render, and where they came from.
 *
 * `source` is surfaced as `data-source` on the grid so a journey can assert which path ran
 * without having to know the clinic's catalogue.
 */
export const resolvePromptCards = (
	tools: XoeTool[] | undefined,
	ctx: PromptContext,
): { cards: PromptCard[]; source: 'tool' | 'fallback' } => {
	const fromTools = tools ? toolsToPromptCards(tools) : [];
	return fromTools.length
		? { cards: fromTools, source: 'tool' }
		: { cards: fallbackPromptCards(ctx), source: 'fallback' };
};
